import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { Description, InterstitialLink, ContentWrap, Banner, InterstitialClose } from './Interstitial.style';
import useTracking from '@gameday/utils/hooks/useTracking';
import { useSelector } from 'react-redux';
import { getDeviceInteraction } from '@gameday/utils/selectors/getDeviceInteraction';
import buildTvUrl from '@gameday/utils/buildTvUrl';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import { resourcePath } from '@gameday/utils/resource-path';
import { ModalTypes } from '../AtBat/atbat.style';
import { getLanguagePath } from '@gameday/utils/languageHelpers';
const { INTERSTITIAL } = ModalTypes;

const Interstitial = ({
    appPath,
    banner,
    gamePk,
    type,
    descriptionKey,
    buttonKey,
    buttonText,
    description,
    date,
    tz,
    gameContext,
    language,
    interstitialUrl,
    slug,
    onClose
}) => {
    const tracking = useTracking();
    const { deviceInteraction } = useSelector(getDeviceInteraction);

    const url = useMemo(() => {
        let url = interstitialUrl;
        const languagePath = getLanguagePath(language);
        if (type === 'watch') {
            url = buildTvUrl({ gamePk, date }, tz, gameContext, language);
        } else if (type === 'video') {
            url = resourcePath.get('videoLink', { appPath, languagePath, slug }) || `/video/${slug}`;
        }
        return url;
    }, [appPath, type, gamePk, date, tz, gameContext, language, slug, interstitialUrl]);

    const handleInterstitialClick = useCallback((e) => {
        const cfg = {
            componentName: ['Interstitial'],
            text: buttonKey,
            activity: 'Interstitial Button Click'
        };
        tracking.track.track_click(cfg);

        switch (type) {
            case 'watch':
                if (deviceInteraction?.tvapp({ gamePk, date })) {
                    e.preventDefault();
                }
                break;
            case 'video':
                if (deviceInteraction?.webview({ url, external: false })) {
                    e.preventDefault();
                }
                break;
            default:
                break;
        }
    }, [tracking, url, gamePk, slug, date, deviceInteraction, buttonKey, type]);

    const translatedButtonText = useTranslator(buttonKey, buttonText);
    const translatedDescription = useTranslator(descriptionKey, description);

    const interstitialButtonHandlers = useMemo(() => ({
        onClick: handleInterstitialClick
    }), [handleInterstitialClick]);

    const closeHandler = useMemo(() => ({ onClick: onClose }), [onClose]);

    return (
        <>
            {banner && <Banner src={banner} />}
            <InterstitialClose
                iconName='close-thin'
                iconSize='20'
                iconColor={'var(--gameday-primary-font-color)'}
                iconHoverColor={'var(--gameday-primary-font-color)'}
                type={INTERSTITIAL}
                handlers={closeHandler}
                ariaLabel={'Close interstitial'}
                isWhite={!!banner}
            />
            {(translatedDescription || translatedButtonText) && (
                <ContentWrap $isPadded={!banner}>
                    {translatedDescription && <Description>{translatedDescription}</Description>}
                    {translatedButtonText && <InterstitialLink
                        handlers={interstitialButtonHandlers}
                        url={url}
                        target='_blank'
                    >
                        {translatedButtonText}
                    </InterstitialLink>}
                </ContentWrap>
            )}
        </>
    );
};

Interstitial.propTypes = {
    appPath: PropTypes.string,
    banner: PropTypes.string,
    gamePk: PropTypes.number,
    type: PropTypes.string,
    descriptionKey: PropTypes.string,
    buttonKey: PropTypes.string,
    buttonText: PropTypes.string,
    description: PropTypes.string,
    interstitialState: PropTypes.object,
    date: PropTypes.string,
    tz: PropTypes.string,
    gameContext: PropTypes.string,
    language: PropTypes.string,
    interstitialUrl: PropTypes.string,
    slug: PropTypes.string,
    onClose: PropTypes.func
};

export default withTheme(Interstitial);
